import { getToken, onMessage } from 'firebase/messaging';
import { messaging } from './firebaseConfig';
import { NotificationService } from './api';

const VAPID_KEY = "BH-qnKeOUja67SR9CONMUEFAt4jnXYmPiUfnYwWKBvhBsGasbXhj-yHuEUSOKZZHizxSujnmHccZckDT0Ee6tf4";

// Removed _tokenRefreshUnsub as onTokenRefresh is not in modular Firebase

export const NotificationServiceWeb = {
    /**
     * Request permission and retrieve the FCM token for the web browser.
     * Registers the token in Firestore and sets up automatic refresh.
     */
    requestAndRegisterToken: async (userId: string): Promise<string | null> => {
        if (!messaging) {
            console.warn('[NotificationServiceWeb] Messaging not initialized — check browser support and HTTPS.');
            return null;
        }

        try {
            // 1. Check or request permission
            let permission = Notification.permission;
            if (permission === 'default') {
                permission = await Notification.requestPermission();
            }
            if (permission !== 'granted') {
                console.warn('[NotificationServiceWeb] Notification permission denied. Users will not receive push alerts.');
                return null;
            }

            // 2. Get FCM token (requires service worker to be active)
            const token = await getToken(messaging, { vapidKey: VAPID_KEY });

            if (!token) {
                console.warn('[NotificationServiceWeb] FCM token unavailable — service worker may not be registered yet.');
                return null;
            }

            // 3. Save token to Firestore (idempotent — safe to call on every login)
            await NotificationService.registerDeviceToken(userId, token, 'web');
            console.log('[NotificationServiceWeb] Web FCM token registered for user', userId);

            // Note: Token rotation is handled by calling requestAndRegisterToken on each app launch
            // instead of using the removed onTokenRefresh from older Firebase versions.

            return token;
        } catch (error: any) {
            // Common causes: service worker missing, insecure origin, blocked by browser extension
            console.error('[NotificationServiceWeb] Error retrieving/registering web token:', error?.code || error?.message || error);
            return null;
        }
    },

    /**
     * Listen for foreground messages and display a browser notification manually
     * (FCM does not auto-show notifications when the app tab is in focus).
     */
    onForegroundMessage: (callback: (payload: any) => void) => {
        if (!messaging) return () => {};
        return onMessage(messaging, (payload) => {
            console.log('[NotificationServiceWeb] Foreground message received:', payload);

            // Show a native notification even when the app is open
            if (Notification.permission === 'granted' && payload.notification) {
                const n = new Notification(payload.notification.title || 'AfroYap', {
                    body: payload.notification.body || '',
                    icon: '/assets/afroyap-icon-192.png',
                    badge: '/assets/afroyap-badge-72.png',
                    tag: (payload.data as any)?.collapseKey || 'afroyap-foreground',
                    renotify: true,
                    silent: false,
                } as any);

                n.onclick = () => {
                    window.focus();
                    n.close();
                };
            }

            callback(payload);
        });
    },

    /**
     * Clean up token refresh listener (call on logout).
     */
    cleanup: () => {
        // No-op

    }
};
